import { useIntl } from "react-intl";
import { AbstractTile } from "@/components/design/AbstractTile";
import { MEDIA_KIND_LABEL, type MediaKind } from "@/components/grow/media-kind";

// Grow · Power detail media section. Renders one curated-resource group
// (videos, podcasts or scripts) from growResource. The underlying genesis
// tables are still empty, so the empty state ("noch keine … hinterlegt") is
// the expected normal case and intentionally not a "coming soon" stub.

export interface GrowMediaItem {
  title: string;
  durationMs?: number | null;
  // Binary thumbnail, base64 without the data: prefix (genesis Binary scalar).
  thumbnail?: string | null;
  mimeType?: string | null;
}

interface Props {
  label: string;
  kind: MediaKind;
  hue: number;
  variant: "lines" | "rings" | "grid";
  items: GrowMediaItem[];
  empty: string;
}

function formatDuration(ms?: number | null): string | null {
  if (!ms || ms <= 0) return null;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")} Min`;
}

export function GrowMediaSection({ label, kind, hue, variant, items, empty }: Props) {
  const intl = useIntl();
  return (
    <div className="box bg-surface p-6">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
        {items.length > 0 ? (
          <div className="field-label text-ink/40">{items.length}</div>
        ) : null}
      </div>

      {items.length === 0 ? (
        <p className="mt-3 text-sm text-ink/55">{empty}</p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-3">
          {items.map((item, i) => {
            const duration = formatDuration(item.durationMs);
            return (
              <div
                key={i}
                className="overflow-hidden rounded-md bg-surface text-left ring-1 ring-ink/5"
              >
                <div className="relative aspect-[16/10]">
                  {item.thumbnail ? (
                    <img
                      src={`data:${item.mimeType || "image/jpeg"};base64,${item.thumbnail}`}
                      alt={item.title}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <AbstractTile variant={variant} hue={hue} className="h-full w-full" />
                  )}
                  <span className="absolute left-2 top-2 rounded bg-ink px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-ink">
                    {intl.formatMessage(MEDIA_KIND_LABEL[kind])}
                  </span>
                  {duration ? (
                    <span className="absolute bottom-2 right-2 rounded bg-ink/80 px-1.5 py-0.5 text-[9px] tracking-widest text-on-ink">
                      {duration}
                    </span>
                  ) : null}
                </div>
                <div className="px-4 py-3">
                  <div className="text-sm font-medium leading-tight">{item.title}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
